import type { ReactNode } from 'react';
import { gameData, spriteFromRef } from '../data/gameData.ts';
import { Check, ColorInput, NumberInput, Section, SpriteThumb, TextInput } from './widgets.tsx';

export type ModValue = { v?: string; r?: string } | null;

const HIDDEN = new Set(['m_Script', 'm_Name', 'm_EditorClassIdentifier', 'm_EditorHideFlags', 'm_ObjectHideFlags', 'serializedVersion', 'tiles', 'palette']);
const BOOLISH = /^(m_)?(is|use|can|has|should|dont|do|enable|enabled|disable|auto|allow|start|loop|flip|lock|show|hide|invert|reverse|only|ignore|pause|warp|kill|destroy|one|single|override|freeze|simulated|isTrigger|used)/i;
const NOT_BOOL = /(count|index|enum|type|layer|order|speed|frames|amount|number|mask|size|time|seconds|id|mode|max|min|offset|radius|direction|value|angle|depth|delay)/i;

export function isBoolField(key: string, v: string) {
  return (v === '0' || v === '1') && (BOOLISH.test(key) || key === 'm_Enabled') && !NOT_BOOL.test(key.replace(/^(m_)?(is|use)/, ''));
}

export function refLabel(v: any, entityName: (ref: string) => string): ReactNode {
  if (!v || String(v.fileID) === '0') return <span className="muted">None</span>;
  if (v.entity || v.ref) return <span className="ref">{entityName(v.entity ?? v.ref)}</span>;
  if (v.guid) {
    const s = spriteFromRef(v);
    if (s) return <span className="ref"><SpriteThumb sprite={s} size={18} /> {s.def.n}</span>;
    const p = gameData().prefabs[v.guid];
    if (p) return <span className="ref">{p.name}</span>;
    return <span className="ref muted">asset {String(v.guid).slice(0, 6)}</span>;
  }
  return <span className="ref muted">internal</span>;
}

interface Props {
  data: any;
  path?: string;
  onSet: (path: string, value: ModValue) => void;
  entityName: (ref: string) => string;
  depth?: number;
  overridden?: (path: string) => boolean;
}

/** Generic editor for Unity serialized component data; edits are reported as propertyPath + value. */
export function FieldEditor({ data, path = '', onSet, entityName, depth = 0, overridden }: Props) {
  if (!data || typeof data !== 'object') return null;
  const keys = Object.keys(data).filter((k) => !HIDDEN.has(k));
  if (!keys.length) return <div className="muted small">No editable fields</div>;
  return (
    <div className="fields">
      {keys.map((k) => (
        <Field key={k} name={k} value={data[k]} path={path ? `${path}.${k}` : k} onSet={onSet} entityName={entityName} depth={depth} overridden={overridden} />
      ))}
    </div>
  );
}

function prettyName(k: string) {
  return k.replace(/^m_/, '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

function Field({ name, value, path, onSet, entityName, depth, overridden }: { name: string; value: any; path: string; onSet: Props['onSet']; entityName: Props['entityName']; depth: number; overridden?: Props['overridden'] }) {
  const label = prettyName(name);
  const mark = overridden?.(path) ? ' overridden' : '';
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number') {
    const v = String(value);
    let input: ReactNode;
    if (isBoolField(name, v)) input = <Check value={v === '1'} onChange={(b) => onSet(path, { v: b ? '1' : '0' })} />;
    else if (v !== '' && Number.isFinite(Number(v))) input = <NumberInput value={Number(v)} step={v.includes('.') ? 0.1 : 1} onChange={(n) => onSet(path, { v: String(n) })} />;
    else input = <TextInput value={v} onChange={(t) => onSet(path, { v: t })} />;
    return (
      <div className={`row${mark}`}>
        <span className="row-label" title={path}>{label}</span>
        <span className="row-value">{input}</span>
      </div>
    );
  }
  if (Array.isArray(value)) {
    return (
      <Section
        title={`${label} [${value.length}]`}
        open={value.length > 0 && value.length <= 8 && depth < 2}
        right={
          <span className="inline">
            <button className="mini" onClick={() => onSet(`${path}.Array.size`, { v: String(Math.max(0, value.length - 1)) })}>−</button>
            <button className="mini" onClick={() => onSet(`${path}.Array.size`, { v: String(value.length + 1) })}>+</button>
          </span>
        }
      >
        {value.map((item, i) => (
          <Field key={i} name={`${i}`} value={item} path={`${path}.Array.data[${i}]`} onSet={onSet} entityName={entityName} depth={depth + 1} overridden={overridden} />
        ))}
      </Section>
    );
  }
  if (typeof value === 'object') {
    if ('fileID' in value || 'ref' in value) {
      return (
        <div className={`row${mark}`}>
          <span className="row-label" title={path}>{label}</span>
          <span className="row-value">
            {refLabel(value, entityName)}
            {String(value.fileID) !== '0' && <button className="mini" title="Clear reference" onClick={() => onSet(path, { r: '' })}>×</button>}
          </span>
        </div>
      );
    }
    const ks = Object.keys(value);
    if (ks.length === 4 && ['r', 'g', 'b', 'a'].every((c) => c in value)) {
      const c: [number, number, number, number] = [Number(value.r), Number(value.g), Number(value.b), Number(value.a)];
      return (
        <div className={`row${mark}`}>
          <span className="row-label" title={path}>{label}</span>
          <span className="row-value">
            <ColorInput
              value={c}
              onChange={(n) => {
                (['r', 'g', 'b', 'a'] as const).forEach((ch, i) => {
                  if (n[i] !== c[i]) onSet(`${path}.${ch}`, { v: String(+n[i].toFixed(4)) });
                });
              }}
            />
          </span>
        </div>
      );
    }
    if (ks.length >= 2 && ks.length <= 4 && ks.every((x) => ['x', 'y', 'z', 'w'].includes(x)) && ks.every((x) => Number.isFinite(Number(value[x])))) {
      return (
        <div className={`row${mark}`}>
          <span className="row-label" title={path}>{label}</span>
          <span className="row-value vec">
            {ks.map((x) => (
              <NumberInput key={x} value={Number(value[x])} step={0.1} width={58} onChange={(n) => onSet(`${path}.${x}`, { v: String(n) })} />
            ))}
          </span>
        </div>
      );
    }
    return (
      <Section title={label} open={depth < 1 && ks.length < 10}>
        <FieldEditor data={value} path={path} onSet={onSet} entityName={entityName} depth={depth + 1} overridden={overridden} />
      </Section>
    );
  }
  return null;
}
